import { NEW_API_URL } from 'constants/Environment';
import { refreshToken, persistTokens } from 'services';
import { goLogout } from 'utils/routes';
import Context from 'managers/Context';
import Cookies from 'js-cookie';

/**
 * Creates a configured fetch instance with automatic token handling and error management
 * @param {Object} config - Configuration object for the fetch instance
 * @param {string} config.baseURL - Base URL for all API requests
 * @returns {Function} Configured fetch instance with additional methods
 */
const createInstance = (config) => {
    /**
     * Enhanced fetch wrapper with automatic authentication and error handling
     * @param {string} endpoint - API endpoint path (will be appended to baseURL)
     * @param {Object} options - Fetch options
     * @param {string} [options.method='GET'] - HTTP method
     * @param {Object} [options.headers={}] - Additional headers
     * @param {string} [options.token] - Custom authorization token (overrides cookie token)
     * @param {Object|string} [options.body] - Request body
     * @returns {Promise<Response>} Fetch response promise
     */
    const innerInstance = (endpoint, options = {}) => {
        const innerToken = `Bearer ${Cookies.get('token')}`;
        const { method, headers, token, body, ...rest } = options;
        const finalOptions = {
            ...rest,
            method,
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'max-age=0, no-cache, must-revalidate, proxy-revalidate',
                ...headers,
                Authorization: innerToken || token,
            },
            body: body || {},
        };

        const url = `${config.baseURL}${endpoint}`;

        return fetch(url, finalOptions)
            .then((response) => {
                if (response && response.status === 401) {
                    // Token expired - attempt to refresh using stored refresh token
                    return refreshToken(Context.config?.refreshToken)
                        .then((data) => {
                            persistTokens(data);
                            // Retry the original request with the new token
                            const retryOptions = {
                                ...options,
                                token: `Bearer ${data.token}`,
                            };
                            return innerInstance(endpoint, retryOptions);
                        })
                        .catch((error) => {
                            // Refresh failed - redirect to logout
                            goLogout();
                            return Promise.reject(error);
                        });
                } else if (response && response.status === 400) {
                    return Promise.reject(response);
                } else {
                    return response;
                }
            })
            .catch((error) => {
                console.error('Fetch error:', error);
                throw error; // Allow error handling at the caller level
            });
    };

    /**
     * Gets the base URL configured for this instance
     * @returns {string} The base URL
     */
    innerInstance.getBaseURL = () => config.baseURL;

    return innerInstance;
};

/**
 * Default fetch instance configured with the application's API URL
 * Provides automatic token management, error handling, and retry logic
 */
const instance = createInstance({
    baseURL: NEW_API_URL,
});

export const fetchInstance = instance;
